import type { T } from '../../../messages';
import type { PageTemplate } from '../../../wire';

/**
 * The Website tab's small translations — wire tokens and machine shapes into
 * the words an operator reads (docs/console-redesign-plan.md §1, principle 2).
 *
 * Kept beside the tab rather than in `vocabulary.ts` because nothing outside
 * the tab says any of them: a reason an answer could not be placed, a domain
 * step, a template's label. A second screen that needs one moves it there.
 */

/**
 * A day as a person writes it — "Oct 7", with the year only when it is not
 * this one. In the operator's own timezone, because "the link works until"
 * is a question about their calendar, not about UTC.
 */
export function shortDate(iso: string | null, locale: string, now = new Date()): string {
  if (iso === null) return '';
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  const sameYear = at.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  }).format(at);
}

/**
 * The short weekday name for the wire's `weekday`, **Monday first** — the
 * owner's week as `@yourstore/domain` sends it. 2024-01-01 was a Monday.
 */
export function weekdayName(weekday: number, locale: string): string {
  const monday = Date.UTC(2024, 0, 1);
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(monday + weekday * 86_400_000),
  );
}

/**
 * Why an answer is not on the page, in words. The token is
 * `@yourstore/intake`'s closed set (`UnplacedReason`); the server's own
 * sentence (`detail`) is shown under it, and a token from a newer server reads
 * as the generic sentence rather than as itself.
 */
export function reasonWords(t: T, reason: string | null): string {
  switch (reason) {
    case 'too-long':
      return t('site.reason.tooLong');
    case 'slot-full':
      return t('site.reason.slotFull');
    case 'no-section':
      return t('site.reason.noSection');
    case 'no-page':
      return t('site.reason.noPage');
    case 'nothing-usable':
      return t('site.reason.nothingUsable');
    default:
      return t('site.reason.other');
  }
}

/** "English", "Spanish" — the owner's form language, in the console's words. */
export function languageName(t: T, locale: string): string {
  const lang = locale.toLowerCase();
  if (lang.startsWith('es')) return t('site.lang.es');
  if (lang.startsWith('en')) return t('site.lang.en');
  return locale;
}

/** A template's label, or its name when the list does not know it. */
export function templateLabel(templates: readonly PageTemplate[], name: string): string {
  return templates.find((one) => one.name === name)?.label ?? name;
}

/**
 * "hero", "room_photos" → "Hero", "Room photos". The shot list's slot names
 * are the only name a pile of photographs has; this makes them read as words.
 */
export function humanize(name: string): string {
  const spaced = name.replace(/[-_]+/g, ' ').trim();
  return spaced === '' ? name : spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Who published, without the actor prefix the audit trail carries. */
export function personOf(actor: string | null): string | null {
  if (actor === null) return null;
  const colon = actor.indexOf(':');
  return colon === -1 ? actor : actor.slice(colon + 1);
}

export function kilobytes(bytes: number): string {
  return `${String(Math.max(1, Math.round(bytes / 1024)))} kB`;
}
